// /src/pages/Consents/ConsentedContactForm.js
import React, { useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  FormHelperText,
  FormGroup
} from "@mui/material";

export default function ConsentedContactForm({ onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    promotional_consent: false,
    exclusive_deals_consent: false,
    terms_consent: true, // Default to true or force user to check
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!formData.email && !formData.phone) {
      setError("Please provide at least an Email or a Phone number.");
      return false;
    }
    if (!formData.promotional_consent && !formData.exclusive_deals_consent) {
        setError("Please select at least one consent type (Promotional or Exclusive Deals).");
        return false;
    }
    if (!formData.terms_consent) {
      setError("Terms and Conditions must be accepted.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError("");
    if (!validate()) return;

    setLoading(true);
    try {
      // Use the authenticated endpoint for admin/user creation
      await axiosInstance.post("/api/user/subscribe", {
        ...formData,
        // Ensure empty strings are sent as null if backend requires, 
        // or backend controller logic handles empty strings.
        email: formData.email || null,
        phone: formData.phone || null
      });

      onSuccess("Consent created successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to create consent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
        Contact Information (Provide at least one)
      </Typography>

      <TextField
        fullWidth
        label="Email Address"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        margin="dense"
        variant="outlined"
      />

      <TextField
        fullWidth
        label="Phone Number"
        name="phone"
        type="tel"
        value={formData.phone}
        onChange={handleChange}
        margin="dense"
        variant="outlined"
        helperText="Format: +1234567890"
      />

      <Box mt={2}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
          Consent Details
        </Typography>
        <FormGroup>
            <FormControlLabel
            control={
                <Checkbox
                checked={formData.promotional_consent}
                onChange={handleChange}
                name="promotional_consent"
                />
            }
            label="Promotional Offers"
            />
            <FormControlLabel
            control={
                <Checkbox
                checked={formData.exclusive_deals_consent}
                onChange={handleChange}
                name="exclusive_deals_consent"
                />
            }
            label="Exclusive Deals"
            />
        </FormGroup>
      </Box>

      <Box mt={2} p={2} bgcolor="action.hover" borderRadius={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.terms_consent}
              onChange={handleChange}
              name="terms_consent"
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              User has agreed to Terms & Conditions and Privacy Policy.
            </Typography>
          }
        />
      </Box>

      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={loading}
        >
          {loading ? "Saving..." : "Create Consent"}
        </Button>
      </Box>
    </Box>
  );
}